/* eslint-disable react-hooks/exhaustive-deps */
import { useLazyQuery, useMutation } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import {
  InvoiceOperationEnum,
  InvoiceStatus,
  Marketplace,
  MarketplaceConfig,
  PaginationInfo,
  PermissionEnum,
  PullAccountsInput,
  RestartInvoicesInput,
  ShopSyncEntityType,
  ShopSyncStatusEnum,
} from "TypesAndInterfaces";
import {
  GET_INVOICE_LOGS,
  GET_SHOP_LOGS,
  LogOutput,
  InvoiceLogsFilter,
  ShopLogsFilter,
} from "../queries";
import Button from "components/Button/Button";
import Dropdown from "components/Dropdown/Dropdown";
import InputField from "components/InputField/InputField";
import "./ShopInvoice.scss";
import "react-tabs/style/react-tabs.css";
import Modal from "components/Modal/Modal";
import TextArea from "components/TextArea/TextArea";
import { displayError, displaySuccess } from "utils/displayNotification";
import { toast } from "react-toastify";
import { useNavigate, createSearchParams } from "react-router-dom";
import { getRoutePath } from 'routes/route-utils';
import { AdyenRoutesName } from 'routes/route-names';
import ControlSyncs from "pages/MarketplaceControl/components/ControlSyncs";
import { initialState } from "utils/validation";
import { restartInvoicesMutation, retryInvoicesMutation, syncShopAccountsMutation } from "pages/MarketplaceControl/graphql";
import { getMarketplaceQuery } from "pages/Marketplaces/queries";
import usePermission from "hooks/usePermission";

enum LogTabPages {
  Invoice,
  Shop,
  Activity
}

export const ShopInvoiceLogs = () => {
  const today = new Date();
  const oneMonthBefore = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
  //#region hooks
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<LogOutput[]>([]);
  const [total, setTotal] = useState(0);
  const [logsPerPage, setLogsPerPage] = useState(10);
  const [index, setIndex] = useState(1);
  const [invoiceFilter, setInvoiceFilter] = useState<InvoiceLogsFilter>({
    isAscending: false,
    fromDate: oneMonthBefore,
  });
  const [shopFilter, setShopFilter] = useState<ShopLogsFilter>({
    fromDate: oneMonthBefore,
    isAscending: false,
  });
  const selectinvoiceRef = useRef(null);
  const selectTypeRef = useRef(null);
  const invoiceRef = useRef(null);
  const shopRef = useRef(null);
  const [currentTab, setCurrentTab] = useState(LogTabPages.Invoice);
  const [marketplaceName, setMarketplaceName] = useState("");
  const [logModalOpened, setLogModalOpened] = useState(false);
  const [selectedLog, setSelectedLog] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [update, setUpdate] = useState(new Date());
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [marketplaceConfig, setMarketplaceConfig] =
    useState<MarketplaceConfig>(initialState);
  const [marketplaceId, setMarketplaceId] = useState<number>();
  const [disabled, setDisabled] = useState(false);
  const { isAuthorized } = usePermission(PermissionEnum.ADMIN);
  //#endregion

  const navigate = useNavigate();

  //#region API CALLS
  const [getInvoiceLogs, { loading: invoiceLoading }] = useLazyQuery<
    { getInvoiceLogs: { logs: LogOutput[]; total: number } },
    {
      pagination: PaginationInfo;
      filter: InvoiceLogsFilter;
      marketplaceId: number;
    }
  >(GET_INVOICE_LOGS, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted: (_data) => {
      if (_data) {
        setButtonDisabled(false);
        setData(_data.getInvoiceLogs.logs);
        setTotal(_data.getInvoiceLogs.total);
      }
    },
    onError: (e) => {
      displayError(e.message);
      setTimeout(() => {
        setButtonDisabled(false);
      }, 1500);
    },
  });

  const [getShopLogs, { loading: shopLoading }] = useLazyQuery<
    { getShopLogs: { logs: LogOutput[]; total: number } },
    {
      pagination: PaginationInfo;
      filter: ShopLogsFilter;
      marketplaceId: number;
    }
  >(GET_SHOP_LOGS, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted: (_data) => {
      if (_data) {
        setButtonDisabled(false);
        setData(_data.getShopLogs.logs);
        setTotal(_data.getShopLogs.total);
      }
    },
    onError: (e) => {
      displayError(e.message);
      setTimeout(() => {
        setButtonDisabled(false);
      }, 1500);
    },
  });

  const [startShopSync] = useMutation<PullAccountsInput>(
    syncShopAccountsMutation,
    {
      onCompleted: (data) => {
        setDisabled(false);
        data && displaySuccess("Sync succeeded");
      },
      onError: (e) => {
        setDisabled(false);
        displayError(e.message);
      },
    }
  );

  const [restartInvoices] = useMutation<RestartInvoicesInput>(
    restartInvoicesMutation,
    {
      onCompleted: (data) => {
        setDisabled(false);
        data && displaySuccess("Restart succeeded");
      },
      onError: (e) => {
        setDisabled(false);
        displayError(e.message);
      },
    }
  );

  const [retryInvoices] = useMutation<RestartInvoicesInput>(
    retryInvoicesMutation,
    {
      onCompleted: (data) => {
        setDisabled(false);
        data && displaySuccess("Retry succeeded");
      },
      onError: (e) => {
        setDisabled(false);
        displayError(e.message);
      },
    }
  );
  const [getMarketplace] = useLazyQuery<
    { getMarketplace: Marketplace },
    { id: number }
  >(getMarketplaceQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (dataMarketplace) => {
      const { config } = dataMarketplace.getMarketplace;
      setMarketplaceConfig(config);
    },
    onError: (e) => {
      displayError(e.message);
    },
  });

  //#endregion

  const fetchData = async (marketplaceId: number) => {
    setButtonDisabled(true);
    switch (currentTab) {
      case LogTabPages.Invoice:
        getInvoiceLogs({
          variables: {
            pagination: {
              index,
              size: logsPerPage,
            },
            filter: invoiceFilter,
            marketplaceId,
          },
        });
        break;
      case LogTabPages.Shop:
        console.log(shopFilter)
        getShopLogs({
          variables: {
            pagination: {
              index,
              size: logsPerPage,
            },
            filter: shopFilter,
            marketplaceId,
          },
        });
        break;
      default:
        return;
    }
  };

  useEffect(() => {
    const id = localStorage.getItem("marketplaceId") ||
      searchParams.get("marketplaceId");
    if (id && !isNaN(+id)) {
      const idNumber = Number(id);
      setMarketplaceId(idNumber);
      getMarketplace({ variables: { id: idNumber } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const marketplaceId =
      localStorage.getItem("marketplaceId") ||
      searchParams.get("marketplaceId");
    const name = searchParams.get("name");
    setMarketplaceName(name || "");
    if (marketplaceId && !isNaN(+marketplaceId)) {
      fetchData(+marketplaceId);
    }
  }, [searchParams, index, logsPerPage, update]);

  const customSort = (rows: any, selector: Function, direction: string) => {
    return rows.sort((rowA: any, rowB: any) => {
      let aField = selector(rowA);
      let bField = selector(rowB);

      let comparison = 0;

      if (aField && aField.props) {
        aField = JSON.stringify(aField.props);
      }

      if (bField && bField.props) {
        bField = JSON.stringify(bField.props);
      }

      if (aField > bField) {
        comparison = 1;
      } else if (aField < bField) {
        comparison = -1;
      }

      return direction === "desc" ? comparison * -1 : comparison;
    });
  };

  const setFilterState = (property: string, value: any) => {
    console.log("entro setFilterState")
    setInvoiceFilter((prev) => ({
      ...prev,
      [property]: value,
    }));
    setShopFilter((prev) => ({
      ...prev,
      [property]: value,
    }));
  };

  const handleDisplayLog = (log: string) => {
    if (log) {
      setSelectedLog(log);
      setLogModalOpened(true);
    }
  };

  const dateSorter = () => (
    <span
      onClick={() => {
        setFilterState("isAscending", !invoiceFilter.isAscending);

        invoiceFilter.isAscending = !invoiceFilter.isAscending;

        const marketplaceId = searchParams.get("marketplaceId");
        const name = searchParams.get("name");
        setMarketplaceName(name || "");
        if (marketplaceId && !isNaN(+marketplaceId)) {
          fetchData(+marketplaceId);
        }
      }}
    >
      <small
        className={
          invoiceFilter.isAscending ? "icon-angle-up" : "icon-angle-down"
        }
      />
      &nbsp;Date
    </span>
  );

  const handleRowClick = (value: string) => {
    navigator.clipboard.writeText(value);
    toast.info(`Copied \`${value}\` to clipboard.`, {
      position: "bottom-center",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const columns: TableColumn<LogOutput>[] = [
    {
      name: "Invoice",
      selector: (row) => row.invoiceId!,
      maxWidth: "50px",
      minWidth: "80px",
      omit: currentTab === LogTabPages.Shop,
    },
    {
      name: "Shop",
      selector: (row) => row.shopId,
      maxWidth: "50px",
      minWidth: "80px",
    },
    {
      name: "Channel",
      selector: (row) => row.channelCode,
      maxWidth: "50px",
      minWidth: "80px",
    },
    {
      name: dateSorter(),
      selector: (row) => dayjs(row.date).format("D/MMM/YYYY HH:mm:ss"),
      maxWidth: "150px",
      minWidth: "80px",
      cell: (row) =>
        row &&
        row.date && (
          <span title={dayjs(row.date).format("D/MMM/YYYY HH:mm:ss")}>
            <p
              onClick={() => {
                handleRowClick(dayjs(row.date).format("D/MMM/YYYY HH:mm:ss"));
              }}
            >
              {dayjs(row.date).format("D/MMM/YYYY HH:mm:ss")}
            </p>
          </span>
        ),
    },
    {
      name: "Type",
      selector: (row) => row.type!,
      maxWidth: "50px",
      minWidth: "80px",
    },
    {
      name: "Status",
      cell: (row) =>
        currentTab === LogTabPages.Invoice
          ? invoiceStatusValues.find((s) => s.value === row.status)?.label
          : shopStatusValues.find((s) => s.value === row.status)?.label,
      maxWidth: "150px",
      minWidth: "80px",
    },
    {
      name: "Reason",
      cell: (row) =>
        row.reason && (
          <span
            className="row-reason-cell"
            onClick={() => handleDisplayLog(row.reason!)}
            title={row.reason}
          >
            <p>{row.reason}</p>
            <small title="Display more..." className="icon-new-tab" />
          </span>
        ),
      maxWidth: "250px",
      minWidth: "80px",
    },
    {
      name: "Request",
      selector: (row) => row.requestId!,
      maxWidth: "50px",
      minWidth: "80px",
      omit: currentTab === LogTabPages.Shop,
    },
    {
      name: "Invocation",
      maxWidth: "250px",
      minWidth: "80px",
      omit: currentTab === LogTabPages.Shop,
      cell: (row) =>
        row &&
        row.invocationId && (
          <span title={row.invocationId.toString()}>
            <p
              onClick={() => {
                handleRowClick(row.invocationId!.toString());
              }}
            >
              {row.invocationId}
            </p>
          </span>
        ),
    },
    {
      name: "Retries remaining",
      selector: (row) => row.retryCount!,
      minWidth: "80px",
      maxWidth: "100px",
      omit: currentTab === LogTabPages.Shop,
    },
    {
      name: "Email sent",
      selector: (row) => (!!row.emailSent ? "YES" : ""),
      maxWidth: "10px",
      minWidth: "80px",
    },
    {
      name: "Email content",
      //selector: (row) => row.emailTextContent?.toString()!,
      cell: (row) =>
        row.emailTextContent && (
          <span
            className="row-reason-cell"
            onClick={() => handleDisplayLog(row.emailTextContent!)}
            title={row.emailTextContent}
          >
            <p>{row.emailTextContent}</p>
            <small title="Display more..." className="icon-new-tab" />
          </span>
        ),
      maxWidth: "200px",
      minWidth: "80px",
    },
  ];

  const changeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number
  ) => {
    setLogsPerPage(currentRowsPerPage);
    setIndex(currentPage);
  };

  const changePage = (page: number, totalRows: any) => {
    setIndex(page);
  };

  const invoiceStatusValues = Object.entries(InvoiceStatus).map((val) => {
    return {
      value: val[0],
      label: val[1],
    };
  });

  const shopStatusValues = Object.entries(ShopSyncStatusEnum).map((val) => {
    return {
      value: val[0],
      label: val[1],
    };
  });

  const shopTypeValues = Object.entries(ShopSyncEntityType).map((val) => {
    return {
      value: val[0],
      label: val[1],
    };
  });

  const invoiceTypeValues = Object.entries(InvoiceOperationEnum).map((val) => {
    return {
      value: val[0],
      label: val[1],
    };
  });

  const handleSearch = (searchParam: string, searchValue: string) => {
    setFilterState(searchParam, Number(searchValue));
  };

  const resetFields = () => {
    setInvoiceFilter({ isAscending: false, fromDate: oneMonthBefore });
    setShopFilter({ isAscending: false, fromDate: oneMonthBefore });
    setIndex(0); // to update the table
    setIndex(1);
    setResetPaginationToggle(!resetPaginationToggle);
    (selectinvoiceRef.current as any)?.clearValue();
    (selectTypeRef.current as any)?.clearValue();
    if (currentTab === LogTabPages.Invoice)
      (invoiceRef.current as any).value = "";
    if (currentTab === LogTabPages.Shop)
      (shopRef.current as any).value = "";
    setUpdate(new Date());
  };

  const sharedDataTable = () => (
    <>
      <DataTable
        sortFunction={customSort}
        pagination
        paginationServer
        paginationTotalRows={total}
        paginationPerPage={logsPerPage}
        onChangeRowsPerPage={changeRowsPerPage}
        onChangePage={changePage}
        columns={columns}
        data={data}
        paginationDefaultPage={index}
        paginationResetDefaultPage={resetPaginationToggle}
        style={{ tableLayout: "fixed", width: "100%" }}
      />
      <div className="buttons-wrapper">
        <Button
          className="button--bordered button--grey"
          type="button"
          content="View Configuration"
          icon="logs"
          onClick={() => {
            const marketplaceId =
              localStorage.getItem("marketplaceId") ||
              searchParams.get("marketplaceId") || 0;
            navigate({
              pathname: getRoutePath(AdyenRoutesName.EDIT_MARKETPLACE),
              search: `${createSearchParams({
                marketplaceId: marketplaceId.toString(),
                name: marketplaceName,
              })}`,
            })
          }
          }
        ></Button>
      </div>
    </>
  );



  const handleShopSync = (ids: string) => {
    const shopIds = ids.split(",").map((id) => Number(id));
    if (marketplaceId && shopIds.length) {
      setDisabled(true);
      startShopSync({
        variables: { params: { marketplaceId, shopIds } },
      });
    }
  };

  const handleRestartInvoices = (ids: string) => {
    const invoiceIds = ids.split(",").map((id) => Number(id));
    if (marketplaceId && invoiceIds.length) {
      setDisabled(true);
      restartInvoices({
        variables: { params: { marketplaceId, invoiceIds } },
      });
    }
  };

  const handleRetryInvoices = (ids: string) => {
    const invoiceIds = ids.split(",").map((id) => Number(id));
    if (marketplaceId && invoiceIds.length) {
      setDisabled(true);
      retryInvoices({
        variables: { params: { marketplaceId, invoiceIds } },
      });
    }
  };

  return (
    <main className="main-container-wrapper">
      <section className="main-container">
        <header className="main-container__header">
          <h2 className="main-container__title">
            {marketplaceName} {LogTabPages[currentTab]} Logs
          </h2>
        </header>
        <div className="main-content marketplace-logs-container">
        {currentTab !== LogTabPages.Activity && (<aside className="main-content__sidebar">
            <header className="main-content__sidebar__header">
              <h4 className="main-content__sidebar__title">Filters</h4>
              <Button
                className="button--small button--rounded button--no-wrap"
                onClick={resetFields}
                type="button"
                content="RESET ALL"
              ></Button>
            </header>
            <div className="filters">
              <Dropdown
                innerRef={selectinvoiceRef}
                options={
                  currentTab === LogTabPages.Invoice
                    ? invoiceStatusValues
                    : shopStatusValues
                }
                placeholder="Select status"
                label="Status"
                className="filters-report-page"
                isMulti={false}
                onChange={(e: any) => e && setFilterState("status", e.value)}
              />
              <Dropdown
                innerRef={selectTypeRef}
                options={currentTab === LogTabPages.Shop ? shopTypeValues : invoiceTypeValues}
                placeholder="Select type"
                label="Type"
                className="filters-report-page"
                isMulti={false}
                onChange={(e: any) => e && setFilterState("type", e.value)}
              />
              <div className="report-datepicker-container">
                <label htmlFor="report">From</label>
                <div className="report-datepicker-input-container">
                  <DatePicker
                  
                    selected={invoiceFilter.fromDate}
                    placeholderText="Select a date"
                    onChange={(date) => setFilterState("fromDate", date)}
                    maxDate={invoiceFilter.toDate}
                  />
                </div>
              </div>
              <div className="report-datepicker-container">
                <label htmlFor="report">To</label>
                <div className="report-datepicker-input-container">
                  <DatePicker
                    selected={invoiceFilter.toDate}
                    placeholderText="Select a date"
                    onChange={(date) => setFilterState("toDate", date)}
                    minDate={invoiceFilter.fromDate}
                  />
                </div>
              </div>
              <div className="logs-id-filters">
                {currentTab === LogTabPages.Invoice && (
                  <span>
                    <InputField
                      innerRef={invoiceRef}
                      numeric
                      label="Invoice Id"
                      defaultValue={invoiceFilter.invoiceId}
                      onChange={(e) =>
                        handleSearch("invoiceId", e.currentTarget.value)
                      }
                    />
                  </span>
                )}
                <span>
                  <InputField
                    innerRef={shopRef}
                    numeric
                    label="Shop Id"
                    defaultValue={invoiceFilter.shopId}
                    onChange={(e) => {
                      handleSearch("shopId", e.currentTarget.value);
                    }}
                  />
                </span>
                <Button
                  className="button--primary"
                  content="Apply Filters"
                  onClick={() => setUpdate(new Date())}
                  disabled={buttonDisabled}
                />
              </div>
            </div>
          </aside>)}
          <div className="main-content__result marketplace-logs-table">
            <Tabs onSelect={() => resetFields()}>
              <TabList>
                <Tab
                  key={LogTabPages.Invoice}
                  onClick={() => setCurrentTab(LogTabPages.Invoice)}
                >
                  {LogTabPages[LogTabPages.Invoice]} Logs
                </Tab>
                <Tab
                  key={LogTabPages.Shop}
                  onClick={() => setCurrentTab(LogTabPages.Shop)}
                >
                  {LogTabPages[LogTabPages.Shop]} Logs
                </Tab>
                {isAuthorized && (<Tab
                  key={LogTabPages.Activity}
                  onClick={() => setCurrentTab(LogTabPages.Activity)}
                >
                  {LogTabPages[LogTabPages.Activity]}
                </Tab>)}
              </TabList>
              <TabPanel>
                {!invoiceLoading ? (
                  sharedDataTable()
                ) : (
                  <div className="adyen-loader" />
                )}
              </TabPanel>
              <TabPanel>
                {!shopLoading ? (
                  sharedDataTable()
                ) : (
                  <div className="adyen-loader" />
                )}
              </TabPanel>
              <TabPanel>
                <ControlSyncs
                  disabled={disabled}
                  config={marketplaceConfig.account}
                  handleShopSync={handleShopSync}
                  handleRetryInvoices={handleRetryInvoices}
                  handleRestartInvoices={handleRestartInvoices}
                  marketplaceId={marketplaceId}
                />
              </TabPanel>
            </Tabs>
          </div>
        </div>
      </section>
      <Modal
        isOpened={logModalOpened}
        title="Detail"
        setIsOpenedCallback={() => setLogModalOpened(false)}
        className="logs-reason-modal"
      >
        <TextArea
          className="logs-reason-details"
          value={selectedLog}
          readOnly
        />
      </Modal>
    </main>
  );
};
